import React, { useState, useEffect } from 'react';
import notify from './utils/notify';
import { confirmLogoutWithUnsent } from './utils/logoutGuard';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import BottomNav from './BottomNav';
import { FiBarChart2, FiBell, FiGrid, FiSettings, FiUser, FiLogOut, FiEdit2, FiCheck, FiX, FiHelpCircle, FiFileText, FiTrash2, FiTrendingUp, FiChevronRight } from 'react-icons/fi';
import { GiGolfTee } from 'react-icons/gi';
import './ProfilePage.css';
import { useApi } from './useApi';
import HandicapKeypadModal from './components/HandicapKeypadModal';
import DeleteAccountModal from './components/DeleteAccountModal';
import ConfirmationModal from './components/ConfirmationModal';
import PageHeader from './components/PageHeader';
import AccountPanel from './components/AccountPanel';
import APP_VERSION from './version';


const ProfilePage = () => {
    const { userProfile, logout, fetchUserProfile, currentUser, currentSociety, setCurrentSociety } = useAuth();
    const { authenticatedFetch } = useApi();
    const navigate = useNavigate();
    
    const [isEditing, setIsEditing] = useState(false);
    const [displayName, setDisplayName] = useState('');
    const [editingHandicap, setEditingHandicap] = useState(null);
    const [savingHandicap, setSavingHandicap] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [ownedSocietiesInfo, setOwnedSocietiesInfo] = useState([]);
    // Held locally so the switch moves under the finger rather than waiting for
    // the round trip and the profile refetch behind it.
    const [statsEnabled, setStatsEnabled] = useState(true);
    const [savingStatsPref, setSavingStatsPref] = useState(false);
    // Leaving a society. The society awaiting confirmation, rather than a
    // boolean, because the card that asked has to be named back to the user —
    // and because the whole card is a tap target that switches society, so the
    // affirmation is what separates leaving from selecting.
    const [leaveTarget, setLeaveTarget] = useState(null);
    const [leaveBusy, setLeaveBusy] = useState(false);
    const [leaveError, setLeaveError] = useState('');

    useEffect(() => {
        if (userProfile) {
            setDisplayName(userProfile.display_name || '');
            // Absent on a profile cached before this shipped — treat as on,
            // which is the column's default.
            setStatsEnabled(userProfile.stats_capture_enabled !== false);
        }
    }, [userProfile]);

    const handleStatsPrefChange = async (next) => {
        const previous = statsEnabled;
        setStatsEnabled(next);
        setSavingStatsPref(true);
        try {
            const res = await authenticatedFetch('/auth/me', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ stats_capture_enabled: next }),
            });
            if (!res.ok) throw new Error('Preference not saved');
            // The scoring screen reads this off userProfile, so it has to be
            // refetched for the change to reach it.
            if (currentUser) await fetchUserProfile(currentUser);
        } catch (err) {
            setStatsEnabled(previous);
            notify.error('Could not save that preference. Please try again.');
        } finally {
            setSavingStatsPref(false);
        }
    };

    const handleLogout = async () => {
        if (!(await confirmLogoutWithUnsent())) return;
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error("Logout failed", error);
        }
    };

    const handleUpdateProfile = async () => {
        try {
            const res = await authenticatedFetch('/auth/me', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ display_name: displayName })
            });
            if (res.ok) {
                setIsEditing(false);
                // Refresh profile in context
                if (currentUser) fetchUserProfile(currentUser);
            } else {
                notify.error("Failed to update profile");
            }
        } catch (error) {
            console.error("Update failed", error);
        }
    };

    const handleUpdateHandicap = async (parsed) => {
        if (!editingHandicap) return;
        setSavingHandicap(true);
        try {
            const res = await authenticatedFetch(`/players/${editingHandicap.playerId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Society-ID': editingHandicap.societyId
                },
                body: JSON.stringify({ handicap: parsed })
            });
            if (res.ok) {
                setEditingHandicap(null);
                if (currentUser) fetchUserProfile(currentUser);
            } else {
                notify.error("Failed to update handicap");
            }
        } catch (error) {
            console.error(error);
        }
        setSavingHandicap(false);
    };

    const openDeleteModal = async () => {
        setDeleteError('');
        setShowDeleteModal(true);
        const owned = (userProfile?.societies || []).filter(s => s.owner_id === userProfile?.id);
        if (owned.length === 0) { setOwnedSocietiesInfo([]); return; }
        const results = await Promise.all(
            owned.map(async (society) => {
                try {
                    const res = await authenticatedFetch(`/societies/${society.id}/members`, {
                        headers: { 'X-Society-ID': society.id },
                    });
                    if (res.ok) {
                        const members = await res.json();
                        const otherAdmins = members.filter(m => m.role === 'ADMIN' && m.user_id !== userProfile.id);
                        return { ...society, successor: otherAdmins[0] || null };
                    }
                } catch {}
                return { ...society, successor: null };
            })
        );
        setOwnedSocietiesInfo(results);
    };

    const handleDeleteAccount = async () => {
        setIsDeleting(true);
        setDeleteError('');
        try {
            const res = await authenticatedFetch('/auth/account', { method: 'DELETE' });
            const data = await res.json();
            if (res.ok) {
                await logout();
                navigate('/login', { state: { accountDeleted: true } });
            } else {
                setDeleteError(data.error || 'Failed to delete account. Please try again.');
                setIsDeleting(false);
            }
        } catch (err) {
            setDeleteError('An unexpected error occurred. Please try again or contact support.');
            setIsDeleting(false);
        }
    };

    const handleSelectSociety = (society) => {
        setCurrentSociety(society);
        // Optional: Navigate to home or leaderboard after selection?
        // navigate('/leaderboard'); 
        // For now just update state and stay on profile
    };

    /* Leave a society of your own accord.

       The same act SocietyManagerModal offers, through the one endpoint — this
       page is where a member goes to look at their memberships, so it is where
       they look for the way out of one. What is kept is the player profile and
       every round attached to it: a society's results are the society's, and
       removing them would rewrite past leaderboards for everyone still in it.

       The server refuses for the OWNER (they would orphan the society) and for
       the LAST ADMIN, and those refusals are shown as given rather than
       reworded here. The owner's button is hidden too, so the refusal is a
       backstop rather than the first they hear of it.

       The profile is refreshed BEFORE switching society, so the one we switch
       to is a society we are still in. */
    const handleLeaveSociety = async () => {
        if (!leaveTarget || leaveBusy) return;
        setLeaveBusy(true);
        setLeaveError('');
        try {
            const res = await authenticatedFetch(`/societies/${leaveTarget.id}/leave`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Explicit: the society being left is not necessarily the
                    // active one, and the route checks the header against the
                    // id in the path.
                    'X-Society-ID': leaveTarget.id,
                },
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setLeaveError(data.error || 'Could not leave the society. Please try again.');
                return;
            }
            const updated = currentUser ? await fetchUserProfile(currentUser) : null;
            if (String(currentSociety?.id) === String(leaveTarget.id)) {
                const remaining = (updated?.societies || [])
                    .find(s => String(s.id) !== String(leaveTarget.id));
                setCurrentSociety(remaining || null);
            }
            setLeaveTarget(null);
        } catch (err) {
            setLeaveError('Could not reach the server, so nothing changed. Check your connection.');
        } finally {
            setLeaveBusy(false);
        }
    };

    // Deliberately the actual owner, not "is an admin": leaving would orphan
    // the society, which is the one case the server will not allow.
    const ownsSociety = (society) => society.owner_id === userProfile?.id;

    const menuItems = [
        { name: 'Leaderboard', route: '/leaderboard', icon: <FiBarChart2 size={24} />, needsSociety: true },
        { name: 'Rounds', route: '/tournaments/scorecard-entry', icon: <FiGrid size={24} />, needsSociety: true },
        { name: 'Play Golf', route: '/play', icon: <GiGolfTee size={24} />, needsSociety: true },
        { name: 'Manage', route: '/setup', icon: <FiSettings size={24} /> },
    ];

    if (!userProfile) return (
        <div className="profile-page-container">
            <PageHeader title="My Profile" backTo={-1} />
            <div style={{padding: '100px 20px', textAlign: 'center'}}>Loading profile...</div>
            <BottomNav menuItems={menuItems} />
        </div>
    );

    return (
        <div className="profile-page-container">
            <PageHeader title="My Profile" backTo={-1} />
            
            <div className="profile-content">
                <div className="profile-card">
                    <div className={`profile-header${isEditing ? ' profile-header--editing' : ''}`}>
                        <div className="profile-avatar">
                            <FiUser size={30} />
                        </div>
                        <div className="profile-info">
                            {isEditing ? (
                                <div className="edit-name-container">
                                    <input 
                                        type="text" 
                                        className="profile-name-input"
                                        value={displayName} 
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        autoFocus
                                    />
                                    <div className="edit-actions">
                                        <button className="icon-btn save-btn" onClick={handleUpdateProfile} aria-label="Save name"><FiCheck size={20} /></button>
                                        <button className="icon-btn cancel-btn" onClick={() => { setIsEditing(false); setDisplayName(userProfile.display_name || ''); }} aria-label="Cancel"><FiX size={20} /></button>
                                    </div>
                                </div>
                            ) : (
                                <div className="display-name-row">
                                    <h2>{userProfile.display_name || 'User'}</h2>
                                    <button className="icon-btn edit-btn" onClick={() => setIsEditing(true)}><FiEdit2 size={16} /></button>
                                </div>
                            )}
                            <p>{userProfile.email}</p>
                        </div>
                    </div>

                    {/* IN THIS CARD, not a card of its own. Who you are and
                        what you are paying for are one answer to one question —
                        "what is my account" — and the plan, its dates and the
                        two buttons that act on it have nowhere else on this
                        page they would not read as a separate concern.

                        Hidden while the name is being edited: the editing row
                        drops the avatar for the width, and the pair of pill
                        buttons under a text field being saved is a second
                        thing to decide about at the wrong moment. */}
                    {!isEditing && <AccountPanel />}
                </div>

                {/* ONE CARD, BECAUSE THEY ARE ONE FEATURE. Logging your shots
                    and reading them back are the two halves of the same thing:
                    the switch decides whether there is anything to read, and
                    the row above it is where you go to read it. They used to be
                    a card and a lone "Preferences" section with a single toggle
                    in it, a heading and a gap apart, which asked the player to
                    join them up themselves.

                    Directly under the profile card, because this is the reason
                    most players open this page: once a round is finalised the
                    scoring screen is closed to them, and filling stats in from
                    the clubhouse is the common case rather than the edge one.

                    Only offered to players who could actually use it; someone
                    whose plan doesn't include stats capture has nothing to
                    switch, and a dead toggle is worse than no toggle. */}
                {userProfile.can_capture_stats !== false && (
                    <div className="perf-card">
                        <button className="perf-entry" onClick={() => navigate('/my-stats')}>
                            <span className="perf-entry-icon"><FiBarChart2 size={20} /></span>
                            <span className="perf-entry-copy">
                                <span className="perf-entry-title">My Performance</span>
                                <span className="perf-entry-sub">
                                    Fairways, greens and putting &mdash; round by round
                                </span>
                            </span>
                            <FiChevronRight size={20} className="perf-entry-chevron" />
                        </button>

                        <label className="pref-row perf-pref" htmlFor="pref-stats-capture">
                            <span className="pref-copy">
                                <span className="pref-title">Track my shot stats</span>
                                {/* One line in either state. The line that
                                    matters when it's off — that nothing is
                                    deleted — is the only thing said then,
                                    rather than carried permanently by a
                                    setting that is usually on. */}
                                <span className="pref-sub">
                                    {statsEnabled
                                        ? 'Fairways, putts and approach distances.'
                                        : 'Hidden everywhere. What you logged is kept.'}
                                </span>
                            </span>
                            <span className="pref-switch">
                                <input
                                    id="pref-stats-capture"
                                    type="checkbox"
                                    checked={statsEnabled}
                                    disabled={savingStatsPref}
                                    onChange={(e) => handleStatsPrefChange(e.target.checked)}
                                />
                                <span className="pref-switch-track" aria-hidden="true">
                                    <span className="pref-switch-thumb" />
                                </span>
                            </span>
                        </label>
                    </div>
                )}

                {/* ITS OWN CARD, not a third row inside My Performance.
                    That card is one feature in two halves — the way into your
                    stats and the switch that decides whether there are any —
                    and it is hidden entirely from a plan without stats
                    capture, which would take the notification settings with
                    it. */}
                <div className="perf-card">
                    <button className="perf-entry" onClick={() => navigate('/profile/notifications')}>
                        <span className="perf-entry-icon"><FiBell size={20} /></span>
                        <span className="perf-entry-copy">
                            <span className="perf-entry-title">Notifications</span>
                            <span className="perf-entry-sub">
                                Round starts, results, birdies &amp; eagles
                            </span>
                        </span>
                        <FiChevronRight size={20} className="perf-entry-chevron" />
                    </button>
                </div>

                <h3>Society Memberships</h3>
                {userProfile.societies && userProfile.societies.length > 0 ? (
                    <div className="societies-list">
                        {userProfile.societies.map(society => {
                            const isActive = currentSociety?.id === society.id;
                            return (
                                /* Two lines: who the society is, then the three
                                   facts about your membership on one meta row.
                                   They were four stacked label/value pairs with
                                   their own rules and dashed dividers, which
                                   made a card ~160px tall — three societies and
                                   the list no longer fit a phone. */
                                <div
                                    key={society.id}
                                    className={`society-card ${isActive ? 'active-society' : ''}`}
                                    onClick={() => handleSelectSociety(society)}
                                >
                                    <div className="society-header">
                                        <h4>{society.name}</h4>
                                        <div className="society-badges">
                                            {isActive && <span className="role-badge active">Active</span>}
                                            <span className={`role-badge ${society.role?.toLowerCase()}`}>{society.role}</span>
                                        </div>
                                    </div>
                                    <div className="society-meta">
                                        {society.player_profile ? (
                                            <>
                                                <span className="sm-item">{society.player_profile.name}</span>
                                                <span className="sm-item">
                                                    Hcp <strong>{society.player_profile.handicap ?? 'N/A'}</strong>
                                                    <button
                                                        className="icon-btn edit-btn sm-edit"
                                                        aria-label={`Edit handicap for ${society.name}`}
                                                        onClick={(e) => { e.stopPropagation(); setEditingHandicap({ societyId: society.id, playerId: society.player_profile.id, handicap: society.player_profile.handicap }); }}
                                                    >
                                                        <FiEdit2 size={13} />
                                                    </button>
                                                </span>
                                            </>
                                        ) : (
                                            <span className="sm-item sm-warn">No linked player</span>
                                        )}
                                        <span className="sm-item">Code <strong>{society.invite_code}</strong></span>
                                    </div>

                                    {/* On its own row under the facts, not in
                                        among them: the whole card is a tap
                                        target that switches society, and a
                                        control that removes you from one
                                        should not sit a thumb's width from the
                                        handicap pencil. The owner gets no
                                        Leave — Shut Down, on Society Profile,
                                        is their door. */}
                                    {!ownsSociety(society) && (
                                        <div className="society-card-actions">
                                            <button
                                                type="button"
                                                className="society-card-leave"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setLeaveError('');
                                                    setLeaveTarget(society);
                                                }}
                                            >
                                                <FiLogOut size={13} /> Leave society
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p style={{textAlign:'center', color:'#666'}}>You are not a member of any societies.</p>
                )}

                {/* Predictions was only ever reachable from the old drawer, so
                    it lives here. Home and Manage are tabs. */}
                <div className="profile-app-links">
                    <button className="profile-app-link" onClick={() => navigate('/betting')}>
                        <FiTrendingUp size={16} /> Predictions
                    </button>
                    <button className="profile-app-link" onClick={() => navigate('/support')}>
                        <FiHelpCircle size={16} /> Support &amp; Help
                    </button>
                    <button className="profile-app-link" onClick={() => navigate('/legal')}>
                        <FiFileText size={16} /> Terms &amp; Privacy
                    </button>
                </div>

                <div className="profile-version">Version {APP_VERSION}</div>

                <button className="logout-button" onClick={handleLogout}>
                    <FiLogOut size={18} /> Log Out
                </button>

                <button className="delete-account-btn" onClick={openDeleteModal}>
                    <FiTrash2 size={15} /> Delete Account
                </button>
            </div>
            <ConfirmationModal
                tone="danger"
                isOpen={!!leaveTarget}
                title="Leave Society?"
                message={`Leave ${leaveTarget?.name || 'this society'}?`}
                confirmText={leaveBusy ? 'Leaving\u2026' : 'Leave society'}
                cancelText="Cancel"
                onConfirm={handleLeaveSociety}
                onCancel={() => { if (!leaveBusy) { setLeaveTarget(null); setLeaveError(''); } }}
            >
                <p className="profile-leave-note">
                    Your scores and results stay with the society. You can rejoin
                    later with the invite code.
                </p>
                {leaveError && <p className="profile-leave-error">{leaveError}</p>}
            </ConfirmationModal>
            {showDeleteModal && (
                <DeleteAccountModal
                    onClose={() => !isDeleting && setShowDeleteModal(false)}
                    onConfirm={handleDeleteAccount}
                    isDeleting={isDeleting}
                    errorMessage={deleteError}
                    ownedSocieties={ownedSocietiesInfo}
                />
            )}
            <HandicapKeypadModal
                isOpen={!!editingHandicap}
                currentHandicap={editingHandicap?.handicap}
                onSave={handleUpdateHandicap}
                onClose={() => setEditingHandicap(null)}
                saving={savingHandicap}
            />
            <BottomNav menuItems={menuItems} />
        </div>
    );
};

export default ProfilePage;
